import * as log from "../utils/log";
import {Serializer} from "../utils/Serializer";

@log.decorator
export class MoneyText extends PIXI.Container {
    log;
    protected _moneyText: PIXI.Text;
    protected _decimalSymbolText: PIXI.Text;
    protected _centsText: PIXI.Text;
    protected _currencyText: PIXI.Text;
    protected _suffix: PIXI.Text;
    private _config: MoneyTextConfig;
    private _diamondImage: PIXI.Sprite;

    private _boundsDebug: PIXI.Graphics;
    private _integerDebug: PIXI.Graphics;
    private _decimalDebug: PIXI.Graphics;
    private _decimalSymbolDebug: PIXI.Graphics;
    private _currencyDebug: PIXI.Graphics;
    private _suffixDebug: PIXI.Graphics;

    private _lastCalculatedWidths: {
        integer: number,
        integerText: number,
        decimal: number,
        decimalText: number,
        decimalSymbol: number,
        decimalSymbolText: number,
        currency: number,
        suffix: number
        suffixText: number
    } = null;

    constructor(config: any) {
        super();

        this._config = new MoneyTextConfig();
        this._config.serialize(config);

        if (this._config.debug) {
            this.createDebugFields();
        }

        this.createTextFields();

        if (this._config.diamond) {
            this._diamondImage = new PIXI.Sprite(this._config.diamond);
            this.addChild(this._diamondImage);
            this._diamondImage.pivot.set(this._diamondImage.texture.width / 2, this._diamondImage.texture.height / 2);
            this._diamondImage.visible = false;
        }
    }

    get moneyString(): string {
        return this._config.moneyString;
    }

    get centsString(): string {
        return this._config.centsString;
    }

    get money(): number {
        return this._config.money;
    }

    get bounds(): any {
        return {width: this._config.bounds.width, height: this._config.bounds.height}
    }

    set isShowingCredits(val: boolean) {
        this._config.isShowingCredits = val;
        this.money = this.money;
    }

    set denomination(val: number) {
        this._config.denomination = val;
        this.money = this.money;
    }

    set prefix(value: string) {
        this._config.prefix = value;
        this._moneyText.text = this._config.moneyString;

        this.fitToBounds();
        this.positionText();
    }

    set suffix(value: string) {
        this._config.suffix = value;
        this._suffix.text = this._config.suffix;
        this._suffix.visible = true;
        this.fitToBounds();
        this.positionText();
    }

    set money(value: number) {
        const moneyLength = this._moneyText.text.length;
        const centsLength = this._centsText.text.length;
        const moneyVisible = this._moneyText.visible;
        const centsVisible = this._centsText.visible;

        this._config.money = value;
        this._moneyText.text = this._config.moneyString;
        this._centsText.text = this._config.centsString;

        if (this._config.shouldHideWhenMoneyValueIsZero && value == 0) {
            this._moneyText.visible = false;
            this._decimalSymbolText.visible = false;
            this._centsText.visible = false;
            this._currencyText.visible = false;
            if (this._diamondImage)
                this._diamondImage.visible = false;
        } else {
            if (this._diamondImage)
                this._diamondImage.visible = true;
            this._moneyText.visible = true;
            this._centsText.visible = this._config.shouldCentsHaveValue;
            this._decimalSymbolText.visible = this._centsText.visible;
            this._currencyText.visible = !this._config.isShowingCredits &&
                this._config.shouldShowCurrency && !this._config.diamond;
        }
        if (this._config.suffix.length != 0)
            this._suffix.visible = true;

        // Recalculate font size and position only if money or cents length or visibility changed.
        if (
            !this._lastCalculatedWidths
            || moneyLength !== this._moneyText.text.length
            || centsLength !== this._centsText.text.length
            || moneyVisible !== this._moneyText.visible
            || centsVisible !== this._centsText.visible
        ) {
            this.fitToBounds();
            this.positionText();
        }

        if (this._config.debug) {
            this._boundsDebug.width = this._config.bounds.width;
            this._boundsDebug.height = this._config.bounds.height;

            this._integerDebug.x = this._moneyText.x;
            this._integerDebug.y = this._moneyText.y;
            this._integerDebug.width = this._lastCalculatedWidths.integer//this._moneyText.width;
            this._integerDebug.height = this._moneyText.height;

            this._decimalSymbolDebug.x = this._decimalSymbolText.x;
            this._decimalSymbolDebug.y = this._decimalSymbolText.y;
            this._decimalSymbolDebug.width = this._lastCalculatedWidths.decimalSymbol;//this._decimalSymbolText.width;
            this._decimalSymbolDebug.height = this._decimalSymbolText.height;

            this._decimalDebug.x = this._centsText.x;
            this._decimalDebug.y = this._centsText.y;
            this._decimalDebug.width = this._lastCalculatedWidths.decimal;//this._centsText.width;
            this._decimalDebug.height = this._centsText.height;

            this._currencyDebug.x = this._currencyText.x;
            this._currencyDebug.y = this._currencyText.y;
            this._currencyDebug.width = this._lastCalculatedWidths.currency//this._currencyText.width;
            this._currencyDebug.height = this._currencyText.height;

            this._suffixDebug.x = this._suffix.x;
            this._suffixDebug.y = this._suffix.y;
            this._suffixDebug.width = this._lastCalculatedWidths.suffix;//this._suffix.width;
            this._suffixDebug.height = this._suffix.height;
        }
    }

    set bounds(value: any) {
        this._config.bounds.height = value.height;
        this._config.bounds.width = value.width;

        this.fitToBounds();
        this.positionText();
    }

    set style(style: any) {
        const moneyStyle = this._moneyText.style;
        const centsStyle = this._centsText.style;
        const currencyStyle = this._currencyText.style;
        const suffixStyle = this._suffix.style;
        for (let key in style) {
            if (key != "fontSize" && key != "font") {
                moneyStyle[key] = style[key];
                centsStyle[key] = style[key];
                currencyStyle[key] = style[key];
                suffixStyle[key] = style[key];
            }
        }

        this._moneyText.style = moneyStyle;
        this._centsText.style = centsStyle;
        this._currencyText.style = currencyStyle;
        this._suffix.style = suffixStyle;
        this._decimalSymbolText.style = moneyStyle;

        this.fitToBounds();
        this.positionText();
    }

    set moneyStyle(style: any) {
        const moneyStyle = this._moneyText.style;
        for (let key in style) {
            if (key != "fontSize" && key != "font") {
                moneyStyle[key] = style[key];
            }
        }

        this._moneyText.style = moneyStyle;

        this.fitToBounds();
        this.positionText();
    }

    set centsStyle(style: any) {
        const centsStyle = this._centsText.style;
        for (let key in style) {
            if (key != "fontSize" && key != "font") {
                centsStyle[key] = style[key];
            }
        }

        this._centsText.style = centsStyle;

        this.fitToBounds();
        this.positionText();
    }

    set currencyStyle(style: any) {
        const currencyStyle = this._currencyText.style;
        for (let key in style) {
            if (key != "fontSize" && key != "font") {
                currencyStyle[key] = style[key];
            }
        }

        this._currencyText.style = currencyStyle;

        this.fitToBounds();
        this.positionText();
    }

    set suffixStyle(style: any) {
        const suffixStyle = this._suffix.style;
        for (let key in style) {
            if (key != "fontSize" && key != "font") {
                suffixStyle[key] = style[key];
            }
        }

        this._suffix.style = suffixStyle;

        this.fitToBounds();
        this.positionText();
    }

    get moneyFontSize(): number {
        return this._moneyText.style.fontSize as number;
    }

    set moneyFontSize(value: number) {
        this._config.moneyFontSize = value;

        this.fitToBounds();
        this.positionText();
    }

    get centsFontSize(): number {
        return this._centsText.style.fontSize as number;
    }

    set centsFontSize(value: number) {
        this._config.centsFontSize = value;

        this.fitToBounds();
        this.positionText();
    }

    get currencyFontSize(): number {
        return this._currencyText.style.fontSize as number;
    }

    set currencyFontSize(value: number) {
        this._config.currencyFontSize = value;

        this.fitToBounds();
        this.positionText();
    }

    set scaleDiamond(value: number) {
        this._config.diamondScaleCoef = value;
        this.fitToBounds();
        this.positionText();
    }

    setAlign(value: string) {
        this._config.align = value
        this.fitToBounds()
        this.positionText()
    }

    private resizeDiamond() {
        this._diamondImage.scale.set(1);
        const diamondScaleToSet: number = (this._moneyText.height / this._diamondImage.height) * this._config.diamondScaleCoef;
        this._diamondImage.width = Math.round(this._diamondImage.width * diamondScaleToSet);
        this._diamondImage.height = Math.round(this._diamondImage.height * diamondScaleToSet);
    }

    private calculateFontProperty() {
        // this causes the "font" property of style to be calculated
        this._moneyText.width;
        this._centsText.width;
        this._currencyText.width;
        this._decimalSymbolText.width;
        this._suffix.width;
    }

    protected createTextFields() {
        var textStyle = {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.moneyFontSize,
            letterSpacing: this._config.letterSpacing
        };

        this._moneyText = new PIXI.Text("", textStyle);
        this._decimalSymbolText = new PIXI.Text(this._config.decimalSymbol, textStyle);

        textStyle = {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.centsFontSize,
            letterSpacing: this._config.letterSpacing
        };

        this._centsText = new PIXI.Text("", textStyle);

        textStyle = {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.currencyFontSize,
            letterSpacing: this._config.letterSpacing
        };

        this._currencyText = new PIXI.Text(" " + this._config.currency, textStyle);

        textStyle = {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.suffixFontSize,
            letterSpacing: this._config.letterSpacing
        };

        this._suffix = new PIXI.Text(this._config.suffix, textStyle);

        this.calculateFontProperty();

        this.addChild(this._moneyText);
        this.addChild(this._decimalSymbolText);
        this.addChild(this._centsText);
        this.addChild(this._currencyText);
        this.addChild(this._suffix);

        this._moneyText.visible = false;
        this._decimalSymbolText.visible = false;
        this._centsText.visible = false;
        this._currencyText.visible = false;
        this._suffix.visible = false;
    }

    protected createDebugFields() {
        this._integerDebug = new PIXI.Graphics();
        this._decimalDebug = new PIXI.Graphics();
        this._decimalSymbolDebug = new PIXI.Graphics();
        this._boundsDebug = new PIXI.Graphics();
        this._currencyDebug = new PIXI.Graphics();
        this._suffixDebug = new PIXI.Graphics();

        this._boundsDebug.beginFill(0x57F900, 0.5);
        // this._boundsDebug.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
        this._boundsDebug.drawRect(0, 0, 1, 1);

        this._integerDebug.beginFill(0xC8002B, 0.5);
        // this._integerDebug.drawRect(this._moneyText.x, this._moneyText.y, this._moneyText.width, this._moneyText.height);
        this._integerDebug.drawRect(0, 0, 1, 1);

        this._decimalSymbolDebug.beginFill(0x0386F2, 0.5);
        // this._decimalSymbolDebug.drawRect(this._decimalSymbolText.x, this._decimalSymbolText.y, this._decimalSymbolText.width, this._decimalSymbolText.height);
        this._decimalSymbolDebug.drawRect(0, 0, 1, 1);

        this._decimalDebug.beginFill(0xD87D00, 0.5);
        // this._decimalDebug.drawRect(this._centsText.x, this._centsText.y, this._centsText.width, this._centsText.height);
        this._decimalDebug.drawRect(0, 0, 1, 1);

        this._currencyDebug.beginFill(0x9F0CF0, 0.5);
        // this._decimalDebug.drawRect(this._centsText.x, this._centsText.y, this._centsText.width, this._centsText.height);
        this._currencyDebug.drawRect(0, 0, 1, 1);

        this._suffixDebug.beginFill(0xFFFF00, 0.5);
        // this._decimalDebug.drawRect(this._centsText.x, this._centsText.y, this._centsText.width, this._centsText.height);
        this._suffixDebug.drawRect(0, 0, 1, 1);

        this.addChild(this._boundsDebug, this._integerDebug, this._decimalSymbolDebug, this._decimalDebug, this._currencyDebug, this._suffixDebug);
    }

    protected positionText() {
        const moneyFontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._moneyText.text, this._moneyText.style);
        const decimalSymbolFontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._decimalSymbolText.text, this._decimalSymbolText.style);
        const centsFontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._centsText.text, this._centsText.style);
        const currencyFontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._currencyText.text, this._currencyText.style);
        const suffixFontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._suffix.text, this._suffix.style);

        const w = this._lastCalculatedWidths;

        if (this._config.bounds.height == 0 || this._config.verticalAlign === "top")
            this._moneyText.y = -moneyFontProps.fontProperties.descent;
        else if (this._config.verticalAlign == "middle") {
            this._moneyText.y = (this._config.bounds.height - this._moneyText.height) / 2;
        } else if (this._config.verticalAlign == "bottom") {
            this._moneyText.y = Math.round(this._config.bounds.height - moneyFontProps.fontProperties.ascent);
        }

        this._suffix.y = this._moneyText.y + moneyFontProps.fontProperties.ascent - suffixFontProps.fontProperties.ascent;

        if (this._config.diamond) {
            this._diamondImage.y = Math.round(this._moneyText.y + this._moneyText.height / 2);
        }

        if (this._config.align == "left") {
            this._moneyText.x = 0;
            if (this._config.diamond) {
                this._diamondImage.x = Math.round(this._diamondImage.width / 2);
                this._moneyText.x += this._diamondImage.width + this._config.spaceBetweenMoneyAndDiamond;
            }
        } else if (this._config.align == "right") {
            if (this._config.diamond) {
                this._moneyText.x = this._config.bounds.width - w.integer;
                this._diamondImage.x = Math.round(this._moneyText.x - this._config.spaceBetweenMoneyAndDiamond -
                    this._diamondImage.width / 2);
            } else {
                this._moneyText.x = this._config.bounds.width - (w.integer + w.decimal + w.decimalSymbol + w.currency + w.suffix);
            }
        } else {
            if (this._config.diamond) {
                this._moneyText.x = (this._config.bounds.width - w.integer) / 2;
                if (!this._config.centerAlignOnlyMoney)
                    this._moneyText.x += (this._diamondImage.width + this._config.spaceBetweenMoneyAndDiamond) / 2;
                this._diamondImage.x = Math.round(this._moneyText.x - this._diamondImage.width / 2 -
                    this._config.spaceBetweenMoneyAndDiamond);
            } else {
                this._moneyText.x = (this._config.bounds.width - this.textWidth) / 2;
                if (this._config.centerAlignOnlyMoney && !this._config.isShowingCredits)
                    this._moneyText.x += w.currency / 2;
            }
        }

        this._suffix.x = this._moneyText.x + w.integer;

        if (this._centsText.visible) {
            this._decimalSymbolText.x = this._moneyText.x + w.integer;
            this._decimalSymbolText.y = this._moneyText.y;

            this._centsText.x = this._decimalSymbolText.x + w.decimalSymbol + this._config.spaceBetweenMoneyAndCents;
            this._centsText.y = this._moneyText.y + moneyFontProps.fontProperties.ascent - centsFontProps.fontProperties.ascent;

            this._suffix.x = this._centsText.x + w.decimalText;
            this._suffix.y = this._centsText.y + centsFontProps.fontProperties.ascent - suffixFontProps.fontProperties.ascent;
        }

        if (this.isCurrencyVisible) {
            if (this._centsText.visible) {
                this._currencyText.x = this._centsText.x + w.decimal;
            } else {
                this._currencyText.x = this._moneyText.x + w.integer;
            }

            this._currencyText.y = this._moneyText.style.strokeThickness / 2 - this._currencyText.style.strokeThickness / 2 +
                this._moneyText.y + moneyFontProps.fontProperties.ascent - currencyFontProps.fontProperties.ascent;
            this._suffix.x = this._currencyText.x + w.currency;
            this._suffix.y = this._currencyText.y + currencyFontProps.fontProperties.ascent - suffixFontProps.fontProperties.ascent;
        }


    }

    get Y(): number {
        return this._moneyText.y;
    }

    get textWidth(): number {
        const widths = this.calculateWidths();
        return widths.integer + widths.decimal + widths.decimalSymbol + widths.currency + widths.suffix;
    }

    get isCurrencyVisible(): boolean {
        return !!(this._config.shouldShowCurrency && !this._config.isShowingCredits && this._currencyText.text.length);
    }

    get isSuffixVisible(): boolean {
        return !!this._config.suffix.length;
    }

    get isDiamondVisible(): boolean {
        return !!this._config.diamond;
    }

    calculateWidths(): {
        integer: number,
        integerText: number,
        decimal: number,
        decimalText: number,
        decimalSymbol: number,
        decimalSymbolText: number,
        currency: number,
        suffix: number
        suffixText: number
    } {
        this.calculateFontProperty();

        const config = this._config;
        const integer = this._moneyText;
        const decimal = this._centsText;
        const decimalSymbol = this._decimalSymbolText;
        const suffix = this._suffix;

        const isDecimalVisible = decimal.visible;
        const isSuffixVisible = this.isSuffixVisible;
        const isCurrencyVisible = this.isCurrencyVisible;
        const isDiamondVisible = this.isDiamondVisible;

        let integerTextWidth = this._moneyText.width//integer.context.measureText(integer.text).width;
        let decimalTextWidth = 0;
        let decimalSymbolTextWidth = 0;
        let suffixTextWidth = 0;

        let integerWidth = integerTextWidth + integer.style.strokeThickness;
        let decimalWidth = 0;
        let decimalSymbolWidth = 0;
        let suffixWidth = 0;
        let currencyWidth = 0;

        if (isDecimalVisible) {
            decimalTextWidth = this._centsText.width//decimal.context.measureText(decimal.text).width;
            decimalSymbolTextWidth = this._decimalSymbolText.width//decimalSymbol.context.measureText(decimalSymbol.text).width;

            decimalWidth = decimalTextWidth + decimal.style.strokeThickness;
            decimalSymbolWidth = decimalSymbolTextWidth + decimalSymbol.style.strokeThickness;
        }

        if (isSuffixVisible) {
            suffixTextWidth = suffix.context.measureText(suffix.text).width;
            suffixWidth = suffixTextWidth + suffix.style.strokeThickness;
        }

        if (isDiamondVisible) {
            currencyWidth = this._diamondImage.width;
        } else if (isCurrencyVisible) {
            currencyWidth = this._currencyText.width//this._currencyText.context.measureText(this._currencyText.text).width;
        }

        this._lastCalculatedWidths = {
            integer: integerWidth,
            integerText: integerTextWidth,
            decimal: decimalWidth,
            decimalText: decimalTextWidth,
            decimalSymbol: decimalSymbolWidth,
            decimalSymbolText: decimalSymbolTextWidth,
            currency: currencyWidth,
            suffix: suffixWidth,
            suffixText: suffixTextWidth
        };

        return this._lastCalculatedWidths;
    }

    dispose() {
        this._moneyText.destroy();
        this._decimalSymbolText.destroy();
        this._centsText.destroy();
        this._currencyText.destroy();

        // this.destroy();
    }

    protected resetFontSizes() {
        this._moneyText.style.fontSize = this._config.moneyFontSize;
        this._moneyText.style = this._moneyText.style;

        this._decimalSymbolText.style.fontSize = this._config.moneyFontSize;
        this._decimalSymbolText.style = this._decimalSymbolText.style;

        this._centsText.style.fontSize = this._config.centsFontSize;
        this._centsText.style = this._centsText.style;

        this._currencyText.style.fontSize = this._config.currencyFontSize;
        this._currencyText.style = this._currencyText.style;

        this._suffix.style.fontSize = this._config.suffixFontSize;
        this._suffix.style = this._suffix.style;

        this.calculateFontProperty();
    }

    protected fitToBounds() {
        this.resetFontSizes();

        if (this._config.diamond)
            this.resizeDiamond();

        if (!this._config.shouldFitToBounds) {
            this.calculateWidths();
            return;
        }

        // TODO: implement scaling of all 3 fields
        let currencyCompensation: number = 0;
        if (this._config.centerAlignOnlyMoney && this._config.align == "center") {
            currencyCompensation = this._config.diamond ? this._diamondImage.width + this._config.spaceBetweenMoneyAndDiamond :
                this._currencyText.context.measureText(this._currencyText.text).width;
        }

        let moneyFontSize: number = this._moneyText.style.fontSize as number;
        let centsFontSize: number = this._centsText.style.fontSize as number;
        let currencyFontSize: number = this._currencyText.style.fontSize as number;
        let suffixFontSize: number = this._suffix.style.fontSize as number;
        let integerStrokeSize: number = this._moneyText.style.strokeThickness as number;
        let decimalStrokeSize: number = this._centsText.style.strokeThickness as number;
        let integerStrokeRation: number = integerStrokeSize ? moneyFontSize / integerStrokeSize : 0;
        let decimalStrokeRation: number = decimalStrokeSize ? centsFontSize / decimalStrokeSize : 0;

        while (this.textWidth > this._config.bounds.width - currencyCompensation && moneyFontSize > this._config.minFontSize) {

            const coefCurrency: number = this._config.diamond ? this._config.diamondScaleCoef : currencyFontSize / moneyFontSize;
            const coefCents: number = centsFontSize / moneyFontSize;
            const coefSuffix: number = suffixFontSize / moneyFontSize;
            moneyFontSize--;

            centsFontSize = moneyFontSize * coefCents;
            currencyFontSize = moneyFontSize * coefCurrency;
            suffixFontSize = moneyFontSize * coefSuffix;

            this._moneyText.style.fontSize = moneyFontSize;
            this._decimalSymbolText.style.fontSize = moneyFontSize;
            this._centsText.style.fontSize = centsFontSize;

            if (integerStrokeRation) {
                this._moneyText.style.strokeThickness = moneyFontSize / integerStrokeRation;
                this._decimalSymbolText.style.strokeThickness = moneyFontSize / integerStrokeRation;
            }

            if (decimalStrokeRation) {
                this._centsText.style.strokeThickness = centsFontSize / decimalStrokeRation;
            }

            this._moneyText.style = this._moneyText.style;
            this._decimalSymbolText.style = this._decimalSymbolText.style;
            this._centsText.style = this._centsText.style;

            this.calculateFontProperty();

            if (this._config.diamond && !this._config.keepDiamondSize) {
                this.resizeDiamond();
            } else if (!this._config.keepCurrencySize) {
                this._currencyText.style.fontSize = currencyFontSize;
                this._currencyText.style = this._currencyText.style;
            }
            if (!this._config.keepSuffixSize) {
                this._suffix.style.fontSize = suffixFontSize;
                this._suffix.style = this._suffix.style;
            }
        }

    }

}

class MoneyTextConfig extends Serializer {
    money: number = 0;
    prefix: string = "";
    suffix: string = "";
    decimalSymbol: string = ".";
    denomination: number = 1;
    currency: string = "";
    centsVisibilityMoneyLimit: number = -1;
    isShowingCredits: boolean = false;
    fontFamily: string = "Arial"; // TODO: font family should be provided in the TextStyle not in the config
    align: string = "left";
    verticalAlign: string = "middle";
    moneyFontSize: number = 24;
    centsFontSize: number = undefined;
    currencyFontSize: number = undefined;
    suffixFontSize: number = undefined;
    spaceBetweenMoneyAndCents: number = 0;
    spaceBetweenMoneyAndDiamond: number = 5;
    shouldShowCurrency: boolean = true;
    centerAlignOnlyMoney: boolean = false;
    shouldHideWhenMoneyValueIsZero: boolean = false;
    minFontSize: number = 6;
    bounds: any = {width: 0, height: 0};
    shouldFitToBounds: boolean = true;
    debug: boolean = false;
    diamond: PIXI.Texture = null;
    diamondScaleCoef: number = 1;
    posDiamondY: number = 0;
    diamondPlay: boolean = false;
    keepCurrencySize: Boolean = false;
    keepDiamondSize: Boolean = false;
    shouldHaveSpaceBetweenThousands: boolean = true;
    hideCents: boolean = false;
    keepSuffixSize: boolean = false;
    /**
         * Whether to allow a different amount of numbers after the decimal point.
         * @example 123.45678 or 123.46
         * @type {boolean}
         */
    allowFlexibleDecimals: boolean = false;
    letterSpacing: number = 0;


    serialize(obj: Object): void {
        super.serialize(obj);

        if (this.centsFontSize == undefined)
            this.centsFontSize = this.moneyFontSize;
        if (this.currencyFontSize == undefined)
            this.currencyFontSize = this.moneyFontSize;
        if (this.suffixFontSize == undefined)
            this.suffixFontSize = this.moneyFontSize;
    }

    get moneyString(): string {
        let str: string = "";
        if (this.isShowingCredits) {
            str = Math.floor(this.money / this.denomination).toString();
        } else if (this.diamondPlay) {
            str = (this.money / 100).toFixed();
        } else {
            str = (this.money / 100).toFixed(2);
            const moneyParts = str.split(".");
            const moneyDigits = moneyParts[0].split('');

            if (this.shouldHaveSpaceBetweenThousands) {
                let counter: number = 3;
                let digitIndex = moneyDigits.length;
                while (--digitIndex > 0) {
                    counter--;
                    if (counter == 0) {
                        counter = 3;
                        moneyDigits.splice(digitIndex, 0, " ");
                    }
                }
            }

            str = moneyDigits.join('');
        }

        if (this.prefix)
            str = this.prefix + str;

        return str;
    }

    get centsString(): string {
        let str = "";
        if (this.shouldCentsHaveValue) {
            if (this.allowFlexibleDecimals) {
                let decimals = 0;
                let tempMoney = this.money;

                while (tempMoney % 100 != 0) {
                    decimals += 1;
                    tempMoney *= 10;
                }
                decimals = Math.max(decimals, 2);

                str = (this.money / 100).toFixed(decimals).split(".")[1];
            } else str = (this.money / 100).toFixed(2).split(".")[1]; 
        }

        return str;
    }

    get shouldCentsHaveValue(): boolean {
        return !this.isShowingCredits && !this.diamondPlay && !this.hideCents &&
            (this.money <= this.centsVisibilityMoneyLimit || this.centsVisibilityMoneyLimit < 0);
    }
}
